import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  BulkDeleteHistoryResponse,
  FailedToDeleteSerial,
  ProductionHistory,
  ScanPullImportReport
} from '../../../Models/ApplicationModels';
import { CustomToastsService } from '../../../Services/custom-toasts.service';
import { MasterDataSeriveService } from '../../../Services/master-data-serive.service';
import { LabelPrintingService } from '../../../Services/label-printing.service';
import { ScanPullImportService } from '../../../Services/scan-pull-import.service';
import { SessionTimeoutService } from '../../../Services/session-timeout.service';

import { AdminBreadcrumbsService } from '../admin-services/admin-breadcrumbs.service';
import { TimeoutError, finalize, timeout } from 'rxjs';

@Component({
  selector: 'app-barcode',
  templateUrl: './barcode.component.html',
  styleUrls: ['./barcode.component.css']
})
export class BarcodeComponent {
  @ViewChild('scanPullModalCloseButton')
  scanPullModalCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('reprintModalCloseButton')
  reprintModalCloseButton?: ElementRef<HTMLButtonElement>;

  @ViewChild('importScanPullInput')
  importScanPullInput?: ElementRef<HTMLInputElement>;

  barcode: string = '';
  barcodeData: Array<ProductionHistory> = [];
  barcodes: Array<string> = [];
  scanPullModalHasSelection: boolean = false;
  deleteModalHasSelection: boolean = false;
  reprintModalHasSelection: boolean = false;
  selectAllChecked: boolean = false;
  failedToScanPullSerials: FailedToDeleteSerial[] = [];
  showFailedScanPullModal: boolean = false;
  scanPullSuccessCount: number = 0;
  showWeekColor: boolean = true;
  isScanPullInProgress: boolean = false;
  isReprintInProgress: boolean = false;
  isImportInProgress: boolean = false;
  importProgressText: string = '';
  showImportReportModal: boolean = false;
  importReport: ScanPullImportReport | null = null;

  constructor(
    private masterservice: MasterDataSeriveService,
    private toastr: CustomToastsService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private permissionsService: NgxPermissionsService,
    private labelPrintingService: LabelPrintingService,
    private sessionTimeoutService: SessionTimeoutService,
    private scanPullImport: ScanPullImportService
  ) {}

  async ngOnInit() {
    const user = this.masterservice.getUserDetailsFromStorage();
    const productionScheduleType = (user?.productionScheduleType ?? '')
      .toString()
      .trim()
      .toLowerCase();
    this.showWeekColor = productionScheduleType !== 'datespecific';

    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Barcode Management', link: '/admin/home/barcode' }
    ]);

    await this.labelPrintingService.initPrinter();
    if (user?.companyID) {
      this.labelPrintingService
        .loadProductLabelFormats(user.companyID)
        .subscribe();
    }
  }

  searchBarcode() {
    this.masterservice
      .barcodeLookup(this.barcode, undefined, true)
      .subscribe((res) => {
        const rawRes = res as any;
        let barcode: ProductionHistory[] = Array.isArray(rawRes)
          ? rawRes
          : (rawRes?.items ?? []);
        if (!barcode || barcode.length === 0) {
          this.toastr.error('Barcode not found.');
          this.barcode = '';
          return;
        }
        let isBarcode = this.barcodeData.find(
          (barcodes) => barcodes.barcode == barcode[0]?.barcode
        );
        if (!isBarcode && barcode.length != 0) {
          this.barcodeData.push(barcode[0]);
        }
        this.barcode = '';
      });
  }

  toggleBarcodes(event: any) {
    const checked = event.target.checked;
    this.selectAllChecked = checked;
    this.barcodeData.forEach((barcode) => {
      if (this.isSelectable(barcode)) {
        barcode.isSelected = checked;
      }
    });
  }

  scanpullAllBarcode() {
    this.barcodes = this.getSelectedBarcodes();
    this.scanPullModalHasSelection = this.barcodes.length > 0;
  }

  scanpullSingleBarcode(barcode: string) {
    this.barcodes = [barcode];
    this.scanpullBarcodes();
  }

  deleteSingleBarcode(barcode: string) {
    this.barcodes = [barcode];
    this.deleteBarcodes();
  }

  reprintAllBarcode() {
    this.barcodes = this.getSelectedBarcodes();
    this.reprintModalHasSelection = this.barcodes.length > 0;
  }

  reprintSingleBarcode(barcode: string): Promise<void> {
    this.barcodes = [barcode];
    return this.reprintBarcodes();
  }

  confirmReprint() {
    if (this.barcodes.length > 0) {
      this.closeReprintModal();
      setTimeout(() => {
        this.reprintBarcodes();
      }, 200);
    }
  }

  async reprintBarcodes(): Promise<void> {
    const attemptedBarcodes = this.getUniqueBarcodes(this.barcodes);
    if (attemptedBarcodes.length === 0 || this.isReprintInProgress) {
      return;
    }

    this.barcodes = [];
    this.isReprintInProgress = true;

    try {
      for (const barcode of attemptedBarcodes) {
        await this.labelPrintingService.printHistoryLabel(barcode, true);
      }
      this.toastr.success(
        attemptedBarcodes.length === 1
          ? 'Barcode sent to printer successfully'
          : `${attemptedBarcodes.length} barcodes sent to printer successfully`
      );
    } catch (err: any) {
      let backendMessage = 'Error occurred while reprinting';
      if (err?.error?.errors?.length > 0) {
        backendMessage = err.error.errors[0].message;
      } else if (err?.error?.detail) {
        backendMessage = err.error.detail;
      } else if (err?.message) {
        backendMessage = err.message;
      }
      this.toastr.error(backendMessage);
    } finally {
      this.isReprintInProgress = false;
      this.resetSelection();
    }
  }

  scanpullBarcodes(): void {
    const attemptedBarcodes = this.getUniqueBarcodes(this.barcodes);
    if (attemptedBarcodes.length === 0 || this.isScanPullInProgress) {
      return;
    }

    this.barcodes = attemptedBarcodes;
    this.isScanPullInProgress = true;

    this.masterservice
      .scanpullBarcodes(attemptedBarcodes)
      .pipe(
        timeout(270_000), // 4.5 minutes — gives the server ample time before the client gives up
        finalize(() => {
          this.isScanPullInProgress = false;
          this.closeScanPullModal();
        })
      )
      .subscribe({
        next: (res: any) => {
          const response = res as BulkDeleteHistoryResponse;
          const successCount =
            response.totalCount - response.failedToDeleteCount;

          if (response.failedToDeleteCount === 0) {
            this.toastr.success(
              `All selected barcodes were scanned and pulled successfully`
            );
            this.removeBarcodesFromList(attemptedBarcodes);
            this.barcodes = [];
            this.resetSelection();
          } else if (
            response.failedToDeleteCount > 0 &&
            response.failedToDeleteCount < response.totalCount
          ) {
            this.scanPullSuccessCount = successCount;
            this.failedToScanPullSerials = response.failedToDeleteSerials;
            this.showFailedScanPullModal = true;
            this.toastr.error(
              `${successCount} barcodes scanned successfully. ${response.failedToDeleteCount} failed to scan pull.`
            );
            this.removeSuccessfulBarcodes(
              attemptedBarcodes,
              response.failedToDeleteSerials
            );
            this.barcodes = [];
            this.resetSelection(response.failedToDeleteSerials);
          } else {
            this.failedToScanPullSerials = response.failedToDeleteSerials;
            this.showFailedScanPullModal = true;
            this.barcodes = [];
            this.resetSelection(response.failedToDeleteSerials);
          }
        },
        error: (err: any) => {
          if (err instanceof TimeoutError) {
            this.toastr.warning(
              'Scan pull is taking longer than expected. ' +
              'Your barcodes may already be processed — please refresh your list to verify.'
            );
          } else {
            let backendMessage = 'Error occurred';
            if (err?.error?.errors?.length > 0) {
              backendMessage = err.error.errors[0].message;
            } else if (err?.error?.detail) {
              backendMessage = err.error.detail;
            } else if (err?.message) {
              backendMessage = err.message;
            }
            this.toastr.error(backendMessage);
          }
          this.barcodes = [];
          this.resetSelection();
        }
      });
  }

  deleteBarcodes(): void {
    const attemptedBarcodes = this.getUniqueBarcodes(this.barcodes);
    if (attemptedBarcodes.length === 0) {
      return;
    }

    this.barcodes = attemptedBarcodes;
    this.masterservice.delHistory(attemptedBarcodes).subscribe({
      next: (res: any) => {
        const response = res as BulkDeleteHistoryResponse;
        const successCount = response.totalCount - response.failedToDeleteCount;

        if (response.failedToDeleteCount === 0) {
          this.toastr.success(
            `All selected barcodes were deleted successfully`
          );
          this.removeBarcodesFromList(attemptedBarcodes);
          this.barcodes = [];
          this.resetSelection();
        } else if (
          response.failedToDeleteCount > 0 &&
          response.failedToDeleteCount < response.totalCount
        ) {
          this.scanPullSuccessCount = successCount;
          this.failedToScanPullSerials = response.failedToDeleteSerials;
          this.showFailedScanPullModal = true;
          this.toastr.error(
            `${successCount} barcodes deleted successfully. ${response.failedToDeleteCount} failed to delete.`
          );
          this.removeSuccessfulBarcodes(
            attemptedBarcodes,
            response.failedToDeleteSerials
          );
          this.barcodes = [];
          this.resetSelection();
        } else {
          this.failedToScanPullSerials = response.failedToDeleteSerials;
          this.showFailedScanPullModal = true;
          this.barcodes = [];
          this.resetSelection();
        }
      },
      error: (err: any) => {
        let backendMessage = 'Error occurred';
        if (err?.error?.errors?.length > 0) {
          backendMessage = err.error.errors[0].message;
        } else if (err?.error?.detail) {
          backendMessage = err.error.detail;
        } else if (err?.message) {
          backendMessage = err.message;
        }
        this.toastr.error(backendMessage);
        this.barcodes = [];
        this.resetSelection();
      }
    });
  }

  removeFromList(i: number) {
    this.barcodeData.splice(i, 1);
    this.updateSelectAllState();
  }

  deleteAllBarcode() {
    this.barcodes = this.getSelectedBarcodes();
    this.deleteModalHasSelection = this.barcodes.length > 0;
  }

  confirmScanPull() {
    if (this.barcodes.length > 0) {
      this.closeScanPullModal();
      setTimeout(() => {
        this.scanpullBarcodes();
      }, 200);
    }
  }

  confirmDelete() {
    if (this.barcodes.length > 0) {
      this.deleteBarcodes();
    }
  }

  openImportDialog(): void {
    if (this.isImportInProgress || this.isScanPullInProgress) {
      return;
    }
    const input = this.importScanPullInput?.nativeElement;
    if (!input) {
      return;
    }
    // Clearing first so re-selecting the same file still fires (change).
    input.value = '';
    input.click();
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!this.scanPullImport.isSupportedFile(file.name)) {
      this.toastr.error('Please select a .csv, .xlsx or .xls file.');
      input.value = '';
      return;
    }

    input.value = '';

    this.scanPullImport.readBarcodeFile(file).subscribe({
      next: ({ rows, barcodes }) => {
        if (barcodes.length === 0) {
          this.toastr.error('No barcodes found in the selected file.');
          return;
        }

        if (rows > ScanPullImportService.MAX_ROWS) {
          this.toastr.error(
            `The file contains ${rows} rows. Please import ` +
              `${ScanPullImportService.MAX_ROWS} or fewer barcodes at a time.`
          );
          return;
        }

        this.runScanPullImport(file.name, rows, barcodes);
      },
      error: (err: Error) => this.toastr.error(err.message)
    });
  }

  closeImportReportModal(): void {
    this.showImportReportModal = false;
    this.importReport = null;
  }

  private runScanPullImport(
    fileName: string,
    rows: number,
    barcodes: string[]
  ): void {
    this.isImportInProgress = true;
    this.importProgressText = '';
    this.sessionTimeoutService.beginProtectedActivity('scan-pull');

    this.scanPullImport
      .importBarcodes(fileName, rows, barcodes)
      .pipe(
        finalize(() => {
          this.isImportInProgress = false;
          this.importProgressText = '';
          this.sessionTimeoutService.endProtectedActivity('scan-pull');
        })
      )
      .subscribe((event) => {
        if (event.type === 'progress') {
          this.importProgressText = event.text;
          return;
        }

        this.importReport = event.report;
        this.showImportReportModal = true;
        this.announceImportOutcome(event.report);

        // Drop pulled barcodes from the grid so rows searched in earlier do not
        // linger in a stale un-pulled state. Rows this run never touched stay put.
        this.removeBarcodesFromList(event.pulledBarcodes);
      });
  }

  private announceImportOutcome(report: ScanPullImportReport): void {
    if (report.stoppedEarly) {
      this.toastr.error(
        `Import stopped after ${report.scanPulledCount} barcode(s) were scan pulled.`
      );
    } else if (report.failedCount === 0) {
      this.toastr.success(
        `${report.scanPulledCount} barcode(s) scan pulled successfully.`
      );
    } else {
      this.toastr.warning(
        `${report.scanPulledCount} barcode(s) scan pulled, ` +
          `${report.failedCount} not processed.`
      );
    }
  }

  private removeSuccessfulBarcodes(
    attemptedBarcodes: string[],
    failedSerials: FailedToDeleteSerial[]
  ): void {
    const failedBarcodes = new Set(failedSerials.map((f) => f.serialNumber));
    const successfulBarcodes = attemptedBarcodes.filter(
      (barcode) => !failedBarcodes.has(barcode)
    );

    this.removeBarcodesFromList(successfulBarcodes);
  }

  closeFailedScanPullModal() {
    this.showFailedScanPullModal = false;
    this.failedToScanPullSerials = [];
    this.scanPullSuccessCount = 0;
  }

  private resetSelection(failedSerials: FailedToDeleteSerial[] = []): void {
    const failedBarcodeSet = new Set(
      failedSerials.map((serial) => serial.serialNumber)
    );

    this.selectAllChecked = false;
    this.barcodes = [];

    this.barcodeData.forEach((barcode) => {
      barcode.isSelected = failedBarcodeSet.has(barcode.barcode);
    });

    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    const selectable = this.barcodeData.filter((barcode) =>
      this.isSelectable(barcode)
    );
    if (selectable.length === 0) {
      this.selectAllChecked = false;
      return;
    }
    this.selectAllChecked = selectable.every((barcode) => barcode.isSelected);
  }

  private isSelectable(barcode: ProductionHistory): boolean {
    return !barcode.isDeleted;
  }

  trackByBarcode(_: number, barcode: ProductionHistory): string {
    return barcode.barcode;
  }

  private getSelectedBarcodes(): string[] {
    return this.getUniqueBarcodes(
      this.barcodeData
        .filter((barcode) => barcode.isSelected)
        .map((barcode) => barcode.barcode)
    );
  }

  private getUniqueBarcodes(barcodes: string[]): string[] {
    return [...new Set(barcodes.filter((barcode) => !!barcode))];
  }

  private removeBarcodesFromList(barcodesToRemove: string[]): void {
    if (barcodesToRemove.length === 0) {
      return;
    }

    const barcodeSet = new Set(barcodesToRemove);
    this.barcodeData = this.barcodeData.filter(
      (item) => !barcodeSet.has(item.barcode)
    );
    this.updateSelectAllState();
  }

  private closeScanPullModal(): void {
    this.scanPullModalCloseButton?.nativeElement.click();
  }

  private closeReprintModal(): void {
    this.reprintModalCloseButton?.nativeElement.click();
  }
}
